/**
 * Count badges on the Messages nav items from the Home prompt counts,
 * on every page (Atlas shows them in its sidebar): Phones = missed callers
 * with no call back plus voicemails nobody listened to, Leads = new leads
 * not contacted, Messages = patient texts and portal messages waiting.
 * Rose when any of them is urgent. Mounted once in the shell; the pages'
 * own badges take over while they are open (navBadges is a stack per id).
 */
import type { HomePrompt, HomePromptKey } from '@velora/contracts/shell';
import { useEffect, useRef } from 'react';
import { useNavBadge } from '../../layout/nav.js';
import { notifications, type NotificationInput } from '../../notifications/store.js';
import { usePermission } from '../../session/session.js';
import { useHomePrompts } from './HomePrompts.js';
import { CHIP_NOUN, PROMPT_META } from './prompts.js';

/**
 * Prompts that also ring the bell (2026-11-01: unanswered texts, missed-call
 * text replies nobody answered, missed callers who only got the automatic
 * text, flagged calls and after-hours AI calls). One line per prompt, keyed
 * by it: it is raised again (unread) when the count grows and leaves the
 * bell when the count reaches zero. Counts only, never a patient.
 */
export const BELL_PROMPTS: readonly HomePromptKey[] = [
  'texts',
  'missed_call_replies',
  'missed_calls',
  'after_hours_ai',
  'ai_calls',
];

const BELL_PREFIX = 'waiting:';

export function waitingBellItems(
  prompts: readonly HomePrompt[],
): Array<NotificationInput & { key: string; count: number }> {
  return prompts
    .filter((p) => BELL_PROMPTS.includes(p.key) && p.count > 0)
    .map((p) => ({
      key: `${BELL_PREFIX}${p.key}`,
      count: p.count,
      title: `${p.count} ${CHIP_NOUN[p.key](p.count)}`,
      body: [PROMPT_META[p.key].title(p.count), p.detail].filter(Boolean).join(' · '),
      tone: p.severity === 'urgent' ? 'danger' : 'warning',
      href: `${p.href}${Object.keys(p.search).length ? `?${new URLSearchParams(p.search).toString()}` : ''}`,
    }));
}

/** Nav module id -> the prompts it counts. */
export const NAV_BADGE_PROMPTS: Readonly<Record<string, readonly HomePromptKey[]>> = {
  phones: ['missed_calls', 'voicemails'],
  leads: ['leads'],
  inbox: ['texts', 'portal_messages'],
};

/** Count and tone of one nav item's badge (pure). */
export function navBadgeFor(
  prompts: readonly HomePrompt[],
  keys: readonly HomePromptKey[],
): { count: number; tone: 'neutral' | 'danger' } {
  const mine = prompts.filter((p) => keys.includes(p.key));
  return {
    count: mine.reduce((n, p) => n + p.count, 0),
    tone: mine.some((p) => p.severity === 'urgent') ? 'danger' : 'neutral',
  };
}

export default function PromptNavBadges() {
  const allowed = usePermission('comms.view', { mode: 'any' });
  const q = useHomePrompts(allowed);
  const prompts = allowed ? (q.data?.prompts ?? []) : [];
  const phones = navBadgeFor(prompts, NAV_BADGE_PROMPTS['phones'] ?? []);
  const leads = navBadgeFor(prompts, NAV_BADGE_PROMPTS['leads'] ?? []);
  const inbox = navBadgeFor(prompts, NAV_BADGE_PROMPTS['inbox'] ?? []);
  useNavBadge('phones', phones.count, phones.tone);
  useNavBadge('leads', leads.count, leads.tone);
  useNavBadge('inbox', inbox.count, inbox.tone);

  const shown = useRef(new Map<string, number>());
  useEffect(() => {
    if (!q.data || !allowed) return;
    const items = waitingBellItems(q.data.prompts);
    const now = new Map(items.map((i) => [i.key, i.count]));
    for (const key of shown.current.keys()) if (!now.has(key)) notifications.resolve(key);
    for (const { count, ...i } of items) {
      const before = shown.current.get(i.key);
      // New, or grown: raise it again. Same or fewer: leave the line as read.
      if (before === undefined || count > before) notifications.notify(i);
    }
    shown.current = now;
  }, [q.data, allowed]);
  return null;
}
